import React, { useState, useRef, useEffect } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

interface MacroData {
  date: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  caloriesTarget: number;
  proteinTarget: number;
  carbsTarget: number;
  fatTarget: number;
}

interface ChartSlideshowProps {
  chartData: MacroData[];
  calculatedGoals?: {
    targetCalories: number;
    protein: { min: number; max: number; target: number };
    carbs: { min: number; max: number; target: number };
    fat: { min: number; max: number; target: number };
  } | null;
}

interface ChartConfig {
  id: string;
  title: string;
  icon: string;
  dataKey: string;
  targetKey: string;
  color: string;
  yAxisLabel: string;
  unit: string;
}

export default function ChartSlideshow({ chartData, calculatedGoals }: ChartSlideshowProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const charts: ChartConfig[] = [
    {
      id: 'calories',
      title: 'Daily Calorie Intake',
      icon: '🔥',
      dataKey: 'calories',
      targetKey: 'caloriesTarget',
      color: '#ff9500',
      yAxisLabel: 'Calories',
      unit: 'cal'
    },
    {
      id: 'protein',
      title: 'Daily Protein Intake',
      icon: '🍗',
      dataKey: 'protein',
      targetKey: 'proteinTarget',
      color: '#0eeaff',
      yAxisLabel: 'Protein (g)',
      unit: 'g'
    },
    {
      id: 'carbs',
      title: 'Daily Carbs Intake',
      icon: '🍞',
      dataKey: 'carbs',
      targetKey: 'carbsTarget',
      color: '#8aff8a',
      yAxisLabel: 'Carbs (g)',
      unit: 'g'
    },
    {
      id: 'fat',
      title: 'Daily Fat Intake',
      icon: '🥑',
      dataKey: 'fat',
      targetKey: 'fatTarget',
      color: '#ff8a8a',
      yAxisLabel: 'Fat (g)',
      unit: 'g'
    }
  ];

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      setCurrentSlide((currentSlide + 1) % charts.length);
    } else if (isRightSwipe) {
      setCurrentSlide(currentSlide === 0 ? charts.length - 1 : currentSlide - 1);
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setCurrentSlide(currentSlide === 0 ? charts.length - 1 : currentSlide - 1);
      } else if (e.key === 'ArrowRight') {
        setCurrentSlide((currentSlide + 1) % charts.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, charts.length]);

  const currentChart = charts[currentSlide];

  if (!calculatedGoals) {
    return (
      <div className="bg-white rounded-lg p-4 shadow">
        <h4 className="text-lg font-semibold text-[#1f1f1f] mb-4 text-center">
          🎯 Macro Tracking
        </h4>
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-2">📊</div>
          <p>Set diet goals to see macro tracking charts</p>
        </div>
      </div>
    );
  }

  const getTargetValue = (chartId: string) => {
    switch (chartId) {
      case 'calories':
        return calculatedGoals.targetCalories;
      case 'protein':
        return calculatedGoals.protein.target;
      case 'carbs':
        return calculatedGoals.carbs.target;
      case 'fat':
        return calculatedGoals.fat.target;
      default:
        return 0;
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 shadow">
      {/* Chart Header with Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCurrentSlide(currentSlide === 0 ? charts.length - 1 : currentSlide - 1)}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Previous chart"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <h4 className="text-lg font-semibold text-[#1f1f1f] text-center flex-1">
          {currentChart.icon} {currentChart.title}
        </h4>

        <button
          onClick={() => setCurrentSlide((currentSlide + 1) % charts.length)}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Next chart"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Chart Container */}
      <div
        ref={containerRef}
        className="relative"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="date" 
              stroke="#666" 
              fontSize={12}
              tick={{ fill: '#666' }}
            />
            <YAxis 
              stroke="#666" 
              fontSize={12}
              tick={{ fill: '#666' }}
              label={{ 
                value: currentChart.yAxisLabel, 
                angle: -90, 
                position: 'insideLeft', 
                style: { textAnchor: 'middle' } 
              }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #d4d4d4',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value: number, name: string) => [`${value}${currentChart.unit}`, name]}
            />
            <Legend />
            
            {/* Actual Intake */}
            <Line 
              type="monotone" 
              dataKey={currentChart.dataKey} 
              stroke={currentChart.color} 
              strokeWidth={3}
              dot={{ fill: currentChart.color, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: currentChart.color, strokeWidth: 2 }}
              name={`Actual ${currentChart.title.split(' ')[2]}`}
            />
            
            {/* Target Line */}
            <Line 
              type="monotone" 
              dataKey={currentChart.targetKey} 
              stroke={currentChart.color} 
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              name={`Target ${currentChart.title.split(' ')[2]}`}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Swipe indicator for mobile */}
        <div className="text-center text-xs text-gray-500 mt-2 md:hidden">
          Swipe to navigate charts
        </div>
      </div>

      {/* Chart Legend */}
      <div className="flex justify-center space-x-4 mt-4 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: currentChart.color }}></div>
          <span>Actual</span>
        </div>
        <div className="flex items-center space-x-2">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ 
              backgroundColor: currentChart.color,
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 2px, ${currentChart.color} 2px, ${currentChart.color} 4px)`
            }}
          ></div>
          <span>Target: {getTargetValue(currentChart.id)}{currentChart.unit}</span>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="flex justify-center space-x-2 mt-4">
        {charts.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === currentSlide ? 'bg-blue-500' : 'bg-gray-300'
            }`}
            aria-label={`Go to chart ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
} 